import Decimal from "decimal.js";
import { JobLineItem, JobPrice, JobPayment, JobV2 } from "../@types/job";
import {
  isProcessingFeeLineItem,
  isTaxableProcessingFeeLineItem,
} from "./lineItem";
import { UserProcessingFee } from "../@types/user";
import { ClientJobV2 } from "../@types/client";

/**
 * Configure Decimal.js for financial calculations
 * These settings ensure consistent and accurate financial calculations:
 * - precision: 20 significant digits for accurate calculations
 * - rounding: ROUND_HALF_UP is standard for financial calculations (5 or greater rounds up)
 * - toExpNeg/Pos: Control scientific notation display thresholds
 */
Decimal.set({
  precision: 20,
  rounding: Decimal.ROUND_HALF_UP,
  toExpNeg: -7, // Only use exponential notation for very small numbers
  toExpPos: 21, // Only use exponential notation for very large numbers
});

// ---- Core Financial Types ----
/**
 * DecimalValue represents a monetary amount with methods for both display and storage
 * This wrapper ensures consistent handling of decimal numbers throughout the application
 * @property value - The underlying Decimal.js value for precise calculations
 * @property formatted - Returns string formatted for display (2 decimal places)
 * @property raw - Returns number for storage in database
 */
type DecimalValue = {
  value: Decimal;
  formatted: () => string; // For display (e.g., "123.45")
  raw: () => number; // For storage (e.g., 123.45)
};

/**
 * Input type for discount calculations
 * Contains all necessary fields to calculate a discount amount
 */
interface DiscountCalculationInput {
  price: number; // Base price before discount
  discountAmount: number | null; // Amount of discount
  discountType: "fixed" | "percentage" | null; // Type of discount
}

/**
 * Input type for deposit calculations
 * Contains all necessary fields to calculate a deposit amount
 */
interface DepositCalculationInput {
  price: number; // Base price (discounted if available)
  depositAmount: number | null; // Amount of deposit
  depositType: "fixed" | "percentage" | null; // Type of deposit
}

/**
 * Input type for final price calculations
 * Contains all necessary fields to calculate price after discounts
 */
interface FinalPriceCalculationInput {
  price: number; // Original price
  discountAmount: number | null; // Amount of discount
  discountType: "fixed" | "percentage" | null; // Type of discount
}

// ---- Utility Functions ----
/**
 * Creates a DecimalValue object from a number-like value
 * Handles null/undefined/invalid inputs safely
 */
export const createDecimalValue = (
  amount: number | string | Decimal | null | undefined
): DecimalValue => {
  // First check if amount is null/undefined
  if (amount === null || amount === undefined) {
    return {
      value: new Decimal(0),
      formatted: () => "0.00",
      raw: () => 0,
    };
  }

  // Then check if it's already a Decimal
  const decimalAmount = amount instanceof Decimal ? amount : toDecimal(amount);

  return {
    value: decimalAmount,
    formatted: () => decimalAmount.toDecimalPlaces(2).toString(),
    raw: () => decimalAmount.toNumber(),
  };
};

/**
 * Safely converts any number-like value to a Decimal
 * Handles null/undefined/invalid inputs by returning zero
 * @param value - Value to convert to Decimal
 * @returns Decimal instance
 * @example
 * toDecimal(123.45) // Returns new Decimal(123.45)
 * toDecimal("123.45") // Returns new Decimal(123.45)
 * toDecimal(null) // Returns new Decimal(0)
 * toDecimal("invalid") // Returns new Decimal(0)
 */
export const toDecimal = (
  value: number | string | Decimal | null | undefined
): Decimal => {
  if (value === null || value === undefined) return new Decimal(0);
  if (value instanceof Decimal) return value;

  try {
    return new Decimal(value);
  } catch (error) {
    console.warn(`Invalid value passed to toDecimal: ${value}`);
    return new Decimal(0);
  }
};

/**
 * Converts a dollar amount to cents (for Stripe API calls)
 * Ensures proper rounding to avoid fractional cents
 * @param dollars - Amount in dollars (e.g., 10.50)
 * @returns Amount in cents as an integer (e.g., 1050)
 * @example
 * dollarsToCents(10.50) // Returns 1050
 * dollarsToCents(10.506) // Returns 1051 (rounds up)
 */
export const dollarsToCents = (dollars: number | null | undefined): number => {
  if (dollars === null || dollars === undefined) {
    return 0;
  }
  return Math.round(toDecimal(dollars).times(100).toNumber());
};

/**
 * Converts cents to dollars (from Stripe API responses)
 * @param cents - Amount in cents (e.g., 1050)
 * @returns Amount in dollars (e.g., 10.50)
 * @example
 * centsToDollars(1050) // Returns 10.50
 * centsToDollars(1051) // Returns 10.51
 */
export const centsToDollars = (cents: number | null | undefined): number => {
  if (cents === null || cents === undefined) {
    return 0;
  }
  return toDecimal(cents).div(100).toNumber();
};

/**
 * Amount for a QuickBooks invoice's Deposit field: money already paid, in
 * whole cents, never above the total QuickBooks computed for the invoice.
 * MountMonitor takes tax on the subtotal while QuickBooks rounds per line, so
 * the two totals can differ by a cent, and a raw grandTotal such as 3699.987
 * on a 3699.98 invoice is rejected with a 6000 business validation error
 * (SOA job #8840, Oct 2026).
 */
export const quickBooksDepositAmount = (
  paid: number | null | undefined,
  invoiceTotal?: number | null,
  lineCount: number = 1
): number | undefined => {
  // Nothing paid, nothing to send: leave the field off as before
  if (paid === null || paid === undefined) {
    return undefined;
  }
  const paidCents = dollarsToCents(paid);
  if (invoiceTotal === null || invoiceTotal === undefined) {
    return centsToDollars(paidCents);
  }
  // invoiceTotal is the total QuickBooks held before this update. Only treat
  // an overshoot as rounding when it is within a cent per line; anything
  // larger means the lines changed in this same save and the old total is
  // stale, so the paid amount goes through as is for QuickBooks to validate.
  const totalCents = dollarsToCents(invoiceTotal);
  const roundingCents = Math.max(1, lineCount);
  const overshoot = paidCents - totalCents;
  const capped = overshoot > 0 && overshoot <= roundingCents;
  return centsToDollars(capped ? totalCents : paidCents);
};

// ---- Amount Calculations ----
/**
 * Basic calculation utilities for working with amounts
 * These functions handle common financial calculations
 */
export const calculateAmount = {
  /**
   * Calculates a percentage of a base amount
   * @example calculateAmount.fromPercentage(100, 15) // 15% of 100 = 15.00
   */
  fromPercentage: (baseAmount: number, percentage: number): DecimalValue => {
    const base = toDecimal(baseAmount);
    const percent = toDecimal(percentage).div(100);
    return createDecimalValue(base.times(percent));
  },

  /**
   * Subtracts a discount from a price
   * @example calculateAmount.withDiscount(100, 20) // 100 - 20 = 80.00
   */
  withDiscount: (price: number, discountAmount: number): DecimalValue => {
    const priceDecimal = toDecimal(price);
    const discountDecimal = toDecimal(discountAmount);
    return createDecimalValue(priceDecimal.minus(discountDecimal));
  },
};

// ---- Line Item Calculations ----
/**
 * Functions for calculating various aspects of individual line items
 * These handle the core business logic for pricing calculations
 */
export const calculateLineItem = {
  /**
   * Calculates the discount amount for a line item
   * Handles both percentage and fixed discounts
   * @returns DecimalValue representing the discount amount
   */
  discount: (input: DiscountCalculationInput): DecimalValue => {
    const price = toDecimal(input.price);
    const discountAmount = input.discountAmount
      ? toDecimal(input.discountAmount)
      : new Decimal(0);

    // Return zero if either price or discount is invalid
    if (!price.isPositive() || !discountAmount.isPositive()) {
      return createDecimalValue(new Decimal(0));
    }

    // Calculate percentage discount if applicable
    if (input.discountType === "percentage") {
      return calculateAmount.fromPercentage(
        price.toNumber(),
        discountAmount.toNumber()
      );
    }

    // Otherwise return fixed discount amount
    return createDecimalValue(discountAmount);
  },

  /**
   * Calculates the deposit amount for a line item
   * Handles both percentage and fixed deposits
   * @returns DecimalValue representing the deposit amount
   */
  deposit: (input: DepositCalculationInput): DecimalValue => {
    const basePrice = toDecimal(input.price);
    const depositAmount = toDecimal(input.depositAmount);

    // Return zero if either price or deposit is invalid
    if (!basePrice.isPositive() || !depositAmount.isPositive()) {
      return createDecimalValue(new Decimal(0));
    }

    // Calculate percentage deposit if applicable. Rounded to cents because
    // the deposit becomes a payment amount: 56.25% of $1,050 stored as
    // 590.625, which Stripe and QuickBooks each rounded on their own.
    if (input.depositType === "percentage") {
      return createDecimalValue(
        calculateAmount
          .fromPercentage(basePrice.toNumber(), depositAmount.toNumber())
          .value.toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
      );
    }

    // Otherwise return fixed deposit amount
    return createDecimalValue(depositAmount);
  },

  /**
   * Calculates the final price after applying discounts
   * @returns DecimalValue representing the final price
   */
  finalPrice: (input: FinalPriceCalculationInput): DecimalValue => {
    const price = toDecimal(input.price);
    const discount = calculateLineItem.discount(input);
    return createDecimalValue(price.minus(discount.value));
  },
};

// ---- Helper Functions for Input Extraction ----
/**
 * Extracts discount calculation inputs from a JobPrice object
 * @param lineItem - Full JobPrice object
 * @returns DiscountCalculationInput with relevant fields
 */
export const getDiscountInputs = (
  lineItem: JobPrice
): DiscountCalculationInput => ({
  price: lineItem.price,
  discountAmount: lineItem.discountAmount,
  discountType: lineItem.discountType,
});

/**
 * Extracts deposit calculation inputs from a JobPrice object
 * Uses discounted price if available, falls back to original price
 * @param lineItem - Full JobPrice object
 * @returns DepositCalculationInput with relevant fields
 */
export const getDepositInputs = (
  lineItem: JobPrice
): DepositCalculationInput => ({
  price: lineItem.discountedPrice || lineItem.price,
  depositAmount: lineItem.depositAmount,
  depositType: lineItem.depositType,
});

// ---- Job Total Calculations ----
/**
 * Functions for calculating totals across all line items in a job
 * These handle aggregating individual line item calculations into job totals
 */

export const calculateJob = {
  /**
   * Calculates the total of all line item prices before discounts
   */
  subtotal: (lineItems: JobLineItem[]): DecimalValue => {
    const total = lineItems.reduce((sum, item) => {
      const itemPrice = toDecimal(item.price);
      return sum.plus(itemPrice);
    }, new Decimal(0));

    return createDecimalValue(total);
  },

  subTotalWithoutProcessingFees: (lineItems: JobLineItem[]): DecimalValue => {
    const total = lineItems.reduce((sum, item) => {
      if (isProcessingFeeLineItem(item)) return sum;
      const finalPrice = calculateLineItem.finalPrice(getDiscountInputs(item));
      return sum.plus(finalPrice.value);
    }, new Decimal(0));

    return createDecimalValue(total);
  },

  subTotalOfProcessingFees: (lineItems: JobLineItem[]): DecimalValue => {
    const total = lineItems.reduce((sum, item) => {
      if (isProcessingFeeLineItem(item)) return sum.plus(toDecimal(item.price));
      return sum;
    }, new Decimal(0));
    return createDecimalValue(total);
  },

  /**
   * Calculates the total of all discounts across all line items
   */
  totalDiscount: (lineItems: JobLineItem[]): DecimalValue => {
    const total = lineItems.reduce((sum, item) => {
      const discount = calculateLineItem.discount(getDiscountInputs(item));
      return sum.plus(discount.value);
    }, new Decimal(0));

    return createDecimalValue(total);
  },

  /**
   * Calculates the subtotal after applying all discounts
   */
  subtotalAfterDiscounts: (lineItems: JobLineItem[]): DecimalValue => {
    const subtotal = calculateJob.subtotal(lineItems);
    const totalDiscount = calculateJob.totalDiscount(lineItems);
    return createDecimalValue(subtotal.value.minus(totalDiscount.value));
  },

  /**
   * Calculates the processing fee amount based on the subtotal & tax for an overall processing fee
   * @param lineItems - All line items excluding processing fee
   * @param feePercentage - Processing fee percentage to apply
   * @param feeFixed - Fixed processing fee amount to add
   * @param taxRate - Tax rate percentage
   * @param jobIncludesTax - Whether the job includes tax
   */
  processingFee: (
    lineItems: JobLineItem[],
    feePercentage: number,
    feeFixed: number,
    taxRate: number,
    jobIncludesTax: boolean
  ): DecimalValue => {
    // 1. Get base total (excluding processing fee)
    const subtotalWithoutProcessingFees =
      calculateJob.subTotalWithoutProcessingFees(lineItems);

    // Calculate tax amount
    const taxAmount = jobIncludesTax
      ? subtotalWithoutProcessingFees.value.times(toDecimal(taxRate).div(100))
      : new Decimal(0);

    // Calculate processing fee on subtotal + tax
    const totalForFee = subtotalWithoutProcessingFees.value.plus(taxAmount);
    let feeAmount = totalForFee.times(toDecimal(feePercentage).div(100));

    // Add fixed amount if provided
    if (feeFixed) {
      feeAmount = feeAmount.plus(toDecimal(feeFixed));
    }

    return createDecimalValue(feeAmount);
  },

  /**
   * Calculates the processing fee amount for a single payment
   * @param amount - The payment amount before processing fee
   * @param feePercentage - Processing fee percentage to apply
   * @returns DecimalValue representing the processing fee amount
   */
  processingFeeForPayment: (
    amount: number,
    feePercentage: number,
    feeFixed: number,
    cap: number | null = null
  ): DecimalValue => {
    const baseAmount = toDecimal(amount);

    // If amount is invalid or zero, return zero
    if (!baseAmount.isPositive()) {
      return createDecimalValue(0);
    }

    // Calculate fee amount based on the payment amount
    let feeAmount = baseAmount.times(toDecimal(feePercentage).div(100));

    if (feeFixed) {
      feeAmount = feeAmount.plus(toDecimal(feeFixed));
    }

    if (cap && isGreaterThan(feeAmount.toNumber(), cap)) {
      feeAmount = toDecimal(cap);
    }

    // Whole cents: the fee is charged and stored as a line item
    return createDecimalValue(
      feeAmount.toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
    );
  },

  /**
   * Calculates the tax amount based on subtotal including processing fee
   * @param lineItems - All line items including processing fee
   * @param taxRate - Tax rate percentage
   * @param processingFeeTaxable - Whether processing fee is taxable
   */
  tax: (
    lineItems: JobLineItem[],
    taxRate: number,
    jobIncludesTax: boolean
  ): DecimalValue => {
    if (!jobIncludesTax) {
      return createDecimalValue(0);
    }

    const taxDecimal = toDecimal(taxRate).div(100);
    // Get subtotal without processing fees as DecimalValue
    const subTotalValue = calculateJob.subTotalWithoutProcessingFees(lineItems);

    // Initialize base amount for tax calculation
    let baseAmount = subTotalValue.value;

    // Detected via the ON_PAYMENT marker in notes rather than the service name.
    // Names are set from the account's own fee label, so they have shown up as
    // "Card Reader Domestic Card for Deposit Payment", lowercase, and even
    // "undefined for Deposit Payment". A name-based check missed all of those,
    // which left the fee sitting in the taxable base while the payment
    // collected no tax on it, so the job kept a few cents after being paid off.
    const taxableProcessingFeeLineItems = lineItems.filter(
      isTaxableProcessingFeeLineItem
    );

    const processingFeesSubtotal =
      taxableProcessingFeeLineItems.length > 0
        ? calculateJob.subTotalOfProcessingFees(taxableProcessingFeeLineItems)
        : createDecimalValue(new Decimal(0));

    baseAmount = baseAmount.plus(processingFeesSubtotal.value);

    // Calculate tax amount
    const taxAmount = baseAmount.times(taxDecimal);
    return createDecimalValue(taxAmount);
  },

  /**
   * Calculates the tax amount for a processing fee
   * @param amount - The processing fee amount (not the payment amount)
   * @param taxRate - Tax rate percentage to apply
   * @param processingFeeTaxable - Whether the processing fee is subject to tax
   * @param jobIncludesTax - Whether the job includes tax
   * @returns DecimalValue representing the tax amount on the processing fee
   * @example
   * calculateJob.taxForProcessingFee(15, 6.75, true, true) // Returns DecimalValue for $1.01 (6.75% of $15)
   */
  taxForProcessingFee: (
    amount: number,
    taxRate: number,
    processingFeeTaxable: boolean,
    jobIncludesTax: boolean
  ): DecimalValue => {
    if (!jobIncludesTax) {
      return createDecimalValue(0); // Return zero if job does not include tax
    }

    if (!processingFeeTaxable) {
      return createDecimalValue(0); // Return zero if processing fee is not taxable
    }

    const baseAmount = toDecimal(amount);
    const taxDecimal = toDecimal(taxRate).div(100);
    // Whole cents: this tax is part of the charged payment total
    return createDecimalValue(
      baseAmount.times(taxDecimal).toDecimalPlaces(2, Decimal.ROUND_HALF_UP)
    );
  },

  applicationFeeForPayment3: (
    baseAmount: number,
    totalChargedAmount: number,
    stripeFeePercent: number = 2.7,
    stripeFeeFixed: number = 0.05,
    mmFeePercent: number = 0.6
  ): DecimalValue => {
    const base = toDecimal(baseAmount);

    // Round the total to cents first (like Stripe does)

    const total = toDecimal(totalChargedAmount).toDecimalPlaces(
      2,
      Decimal.ROUND_HALF_UP
    );

    if (base.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).equals(total)) {
      // Merchant is absorbing fees, so we take our cut first
      const ourFee = total.times(mmFeePercent / 100);
      return createDecimalValue(ourFee);
    }

    // Calculate what Stripe will take
    const stripeFeeRaw = total
      .times(toDecimal(stripeFeePercent).div(100))
      .plus(toDecimal(stripeFeeFixed));

    // Round Stripe's fee to cents (like they do)
    const stripeFee = stripeFeeRaw.toDecimalPlaces(2, Decimal.ROUND_HALF_UP);

    // Calculate our maximum allowed fee (0.6% of  total)
    const ourMaxFee = total.times(toDecimal(mmFeePercent).div(100));

    // Calculate what's actually available after  merchant and Stripe are paid
    const availableFee = total.minus(base).minus(stripeFee);

    // Take the LESSER of: our max fee OR what's available
    const ourFee = ourMaxFee.lessThan(availableFee) ? ourMaxFee : availableFee;

    // Ensure we don't go negative
    const finalFee = ourFee.isNegative() ? toDecimal(0) : ourFee;

    return createDecimalValue(finalFee);
  },

  /**
   * Calculates the MountMonitor application fee for ACH payments
   * When customer is charged 1% flat, but Stripe takes 0.8% capped at $5
   * @param paymentAmount - The base payment amount (before any fees)
   * @returns DecimalValue representing MM's application fee in dollars
   */

  applicationFeeForACHPayment3: (
    baseAmount: number,
    totalChargedAmount: number
  ): DecimalValue => {
    const base = toDecimal(baseAmount);

    // Round the total to cents first (like Stripe does)
    const total = toDecimal(totalChargedAmount).toDecimalPlaces(
      2,
      Decimal.ROUND_HALF_UP
    );

    // Calculate Stripe's fee (0.8% capped at $5)
    const stripePercentFeeRaw = total.times(toDecimal(0.8).div(100));
    const stripeCap = toDecimal(5);
    const stripePercentFee = stripePercentFeeRaw.lessThan(stripeCap)
      ? stripePercentFeeRaw
      : stripeCap;

    // Round Stripe's fee to cents (like they do)
    const stripeFee = stripePercentFee.toDecimalPlaces(
      2,
      Decimal.ROUND_HALF_UP
    );

    // Check if merchant is absorbing fees (no fee pass-through)
    if (base.toDecimalPlaces(2, Decimal.ROUND_HALF_UP).equals(total)) {
      // Merchant absorbs fees - we take what would be left from 1% after Stripe
      // Round the 1% fee to cents first
      const onePercentFee = total
        .times(toDecimal(1).div(100))
        .toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
      const ourFee = onePercentFee.minus(stripeFee);

      return createDecimalValue(ourFee);
    }

    // Customer pays fees - they paid exactly 1% on top of base
    // The 1% fee collected should already be rounded to cents
    const totalFeeCollected = total.minus(base);

    // Our fee is the 1% collected minus what Stripe takes
    const ourFee = totalFeeCollected.minus(stripeFee);

    // Ensure we don't go negative
    const finalFee = ourFee.isNegative() ? toDecimal(0) : ourFee;

    return createDecimalValue(finalFee);
  },

  /**
   * Calculates the total deposit amount across all line items
   */
  totalDeposit: (lineItems: JobLineItem[]): DecimalValue => {
    const total = lineItems.reduce((sum, item) => {
      const deposit = calculateLineItem.deposit(getDepositInputs(item));
      return sum.plus(deposit.value);
    }, new Decimal(0));

    return createDecimalValue(total);
  },

  /**
   * Calculates the grand total following the correct order:
   * 1. Base amount
   * 2. Processing fee
   * 3. Tax on subtotal
   */
  grandTotal: (
    lineItems: JobLineItem[],
    taxRate: number,

    jobIncludesTax: boolean
  ): DecimalValue => {
    const subtotalAfterDiscounts =
      calculateJob.subtotalAfterDiscounts(lineItems);
    const tax = calculateJob.tax(
      lineItems,
      taxRate,

      jobIncludesTax
    );

    return createDecimalValue(subtotalAfterDiscounts.value.plus(tax.value));
  },

  /**
   * Calculates the total of all payments made
   * Excludes deleted payments and accounts for refunds
   */
  totalPayments: (payments: JobPayment[]): DecimalValue => {
    const total = payments.reduce((sum, payment) => {
      // Skip deleted payments
      if (payment.deleted) {
        return sum;
      }
      // old payments don't have total
      const paymentTotal = toDecimal(payment.total || payment.amount);
      // Subtract any refunds to get effective amount
      const refunded = toDecimal(payment.totalRefunded || 0);
      return sum.plus(paymentTotal.minus(refunded));
    }, new Decimal(0));

    return createDecimalValue(total);
  },

  /**
   * Calculates the remaining balance after applying all payments
   */
  remainingBalance: (
    lineItems: JobLineItem[],
    taxRate: number,
    payments: JobPayment[],
    jobIncludesTax: boolean
  ): DecimalValue => {
    const grandTotal = calculateJob.grandTotal(
      lineItems,
      taxRate,
      jobIncludesTax
    );
    const totalPayments = calculateJob.totalPayments(payments);

    return createDecimalValue(grandTotal.value.minus(totalPayments.value));
  },

  /**
   * Takes a fully calculated job with precise values and returns a version
   * formatted for customer-facing display. This function does not re-perform
   * any calculations; it only rounds existing values to two decimal places.
   */
  asPerClientFacingInvoice: (job: JobV2) => {
    const formatValue = (val: number | null | undefined) =>
      toDecimal(val || 0)
        .toDecimalPlaces(2)
        .toNumber();

    const formattedLineItems = job.lineItems.map((li: JobLineItem) => ({
      ...li,
      price: formatValue(li.price),
      discountedPrice:
        li.discountedPrice !== null && li.discountedPrice !== undefined
          ? formatValue(li.discountedPrice)
          : null,
      deposit: formatValue(li.deposit),
      discount: formatValue(li.discount),
    }));

    const formattedPayments = job.payments.map((p: JobPayment) => ({
      ...p,
      amount: formatValue(p.amount),
      processingFee: formatValue(p.processingFee),
      tax: formatValue(p.tax),
      total: formatValue(p.total),
    }));

    return {
      ...job,
      subtotal: formatValue(createDisplaySubTotal(job.lineItems)),
      totalDiscount: formatValue(job.totalDiscount),
      subtotalAfterDiscounts: formatValue(
        createDisplaySubTotal(job.lineItems, true)
      ),
      totalTax: formatValue(job.totalTax),
      grandTotal: formatValue(
        calculateDisplayGrandTotal(
          job.lineItems,
          job.subtotalAfterDiscounts,
          job.totalTax,
          job.grandTotal
        )
      ),
      totalDeposit: formatValue(job.totalDeposit),
      remainingBalance: toNearZero(
        calculateDisplayRemainingBalance(
          job.lineItems,
          job.subtotalAfterDiscounts,
          job.totalTax,
          job.grandTotal,
          job.payments,
          job.remainingBalance
        )
      ),
      taxRate: job.taxRate,
      lineItems: formattedLineItems,
      payments: formattedPayments,
    };
  },
  // This function takes the customer-facing, rounded invoice data (the object returned by asPerInvoice)
  // and simulates what a customer might do:
  // 1. Sums up all the line item totals to ensure it matches the displayed subtotal.
  // 2. Subtracts the displayed discount, adds displayed tax, to confirm the displayed grand total.
  // 3. Sums all the payment totals and ensures that subtracting from the grand total leaves no remaining balance.
  //
  // Note: This function doesn't re-calculate any business logic. It only sums the already rounded
  // values to demonstrate that the displayed values align correctly.

  // A small helper for equality checks using Decimal.js

  simulateCustomerCheck: (invoice: {
    subtotal: number;
    subtotalAfterDiscounts: number;
    totalDiscount: number;
    totalTax: number;
    grandTotal: number;
    totalDeposit: number;
    remainingBalance: number;
    lineItems: { price: number; discountedPrice?: number | null }[];
    payments: { total: number }[];
  }) => {
    const decimalEquals = (a: number, b: number, epsilon = 0.01) => {
      const diff = new Decimal(a).minus(b).abs();
      return diff.lessThanOrEqualTo(epsilon);
    };

    let lineItemSum = new Decimal(0);
    for (const li of invoice.lineItems) {
      const itemTotal =
        li.discountedPrice !== null && li.discountedPrice !== undefined
          ? li.discountedPrice
          : li.price;
      lineItemSum = lineItemSum.plus(itemTotal);
    }

    let paymentsSum = new Decimal(0);
    for (const p of invoice.payments) {
      // old payments don't have total
      paymentsSum = paymentsSum.plus(toDecimal(p.total || (p as any).amount));
    }

    // Now just use decimalEquals to compare
    const checkSubtotalMatches = decimalEquals(
      lineItemSum.toNumber(),
      invoice.subtotalAfterDiscounts
    );

    const checkGrandTotalMatches = decimalEquals(
      new Decimal(invoice.subtotalAfterDiscounts)
        .plus(invoice.totalTax)
        .toNumber(),
      invoice.grandTotal
    );

    // Checking remaining balance
    const calcRemaining = new Decimal(invoice.grandTotal).minus(paymentsSum);
    const checkRemainingBalance = decimalEquals(
      calcRemaining.toNumber(),
      invoice.remainingBalance
    );

    return {
      subtotal: invoice.subtotalAfterDiscounts,
      totalTax: invoice.totalTax,
      grandTotal: invoice.grandTotal,
      paymentsSum: paymentsSum.toNumber(),
      remainingBalance: invoice.remainingBalance,
      checkSubtotalMatches,
      checkGrandTotalMatches,
      checkRemainingBalance,
    };
  },
};

// ---- Basic Mathematical Operations ----
/**
 * Safely adds two numbers using Decimal.js for precision
 * @param a - First number
 * @param b - Second number
 * @returns number result of addition
 */
export const calculateAddition = (a: number, b: number): number => {
  return toDecimal(a).plus(toDecimal(b)).toNumber();
};

/**
 * Safely subtracts two numbers using Decimal.js for precision
 * @param a - First number (minuend)
 * @param b - Second number (subtrahend)
 * @returns number result of subtraction
 */
export const calculateSubtraction = (a: number, b: number): number => {
  return toDecimal(a).minus(toDecimal(b)).toNumber();
};

/**
 * Safely multiplies two numbers using Decimal.js for precision
 * @param a - First number
 * @param b - Second number
 * @returns number result of multiplication
 */
export const calculateMultiplication = (a: number, b: number): number => {
  return toDecimal(a).times(toDecimal(b)).toNumber();
};

/**
 * Rounds a value to the nearest multiple of step using Decimal.js
 * (ROUND_HALF_UP). A step of 0 or less returns the value unchanged.
 * @example roundToNearest(547.83, 1) // 548
 * @example roundToNearest(547.83, 5) // 550
 */
export const roundToNearest = (value: number, step: number): number => {
  if (!step || step <= 0) return toDecimal(value).toNumber();
  const stepDecimal = toDecimal(step);
  return toDecimal(value)
    .dividedBy(stepDecimal)
    .toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
    .times(stepDecimal)
    .toNumber();
};

/**
 * Distributes a total across weighted shares so the parts sum EXACTLY to the
 * total, using the largest remainder method.
 *
 * Dividing proportionally and rounding each share to cents leaves a remainder
 * of a cent or two. Largest remainder hands those leftover cents to the shares
 * whose fractional part was biggest, which keeps every share within a cent of
 * its proportional value and, crucially, makes the parts add up.
 *
 * Exactness is the whole point. Split payments divide a processing fee, that
 * fee's tax, and (on cross-currency charges) the converted amount across the
 * jobs a single payment covers. If those parts do not sum to what was actually
 * charged, refunding the last portion is either rejected by Stripe as an
 * over-refund or leaves a cent stranded forever.
 *
 * Ties break on original order, so the same input always gives the same output.
 * Non-positive weights fall back to an even split rather than returning zeros,
 * which would silently lose the total.
 *
 * @param total - Amount to distribute, in currency units
 * @param weights - Relative share of each recipient, e.g. each portion's amount
 * @returns One amount per weight, each at 2dp, summing exactly to total
 * @example allocateByLargestRemainder(10, [1, 1, 1]) // [3.34, 3.33, 3.33]
 */
export const allocateByLargestRemainder = (
  total: number,
  weights: number[]
): number[] => {
  if (weights.length === 0) return [];

  const sign = total < 0 ? -1 : 1;
  const totalCents = toDecimal(Math.abs(total))
    .times(100)
    .toDecimalPlaces(0)
    .toNumber();

  const positiveWeights = weights.map((weight) => (weight > 0 ? weight : 0));
  const weightSum = positiveWeights.reduce((sum, weight) => sum + weight, 0);
  const effectiveWeights =
    weightSum > 0 ? positiveWeights : weights.map(() => 1);
  const effectiveWeightSum =
    weightSum > 0 ? weightSum : effectiveWeights.length;

  const shares = effectiveWeights.map((weight, index) => {
    const exact = toDecimal(totalCents).times(weight).div(effectiveWeightSum);
    const floored = exact.floor();
    return {
      index,
      cents: floored.toNumber(),
      remainder: exact.minus(floored).toNumber(),
    };
  });

  const leftover =
    totalCents - shares.reduce((sum, share) => sum + share.cents, 0);

  const byRemainder = [...shares].sort(
    (a, b) => b.remainder - a.remainder || a.index - b.index
  );
  for (let i = 0; i < leftover; i++) {
    byRemainder[i % byRemainder.length].cents += 1;
  }

  return shares.map((share) => centsToDollars(share.cents * sign));
};

/**
 * Safely compares if first number is greater than second number
 * @param a - First number
 * @param b - Second number
 * @returns boolean indicating if a > b
 */
export const isGreaterThan = (a: number, b: number): boolean => {
  return toDecimal(a).greaterThan(toDecimal(b));
};

/**
 * Safely compares if first number is less than second number
 * @param a - First number
 * @param b - Second number
 * @returns boolean indicating if a < b
 */
export const isLessThan = (a: number, b: number): boolean => {
  return toDecimal(a).lessThan(toDecimal(b));
};

/**
 * Safely compares if first number is greater than or equal to second number
 * Uses Decimal.js for precise comparison to avoid floating point errors
 * @param a - First number
 * @param b - Second number
 * @returns boolean indicating if a >= b
 * @example
 * isGreaterThanOrEqual(0.1 + 0.2, 0.3) // Returns true
 * isGreaterThanOrEqual(5, 5) // Returns true
 * isGreaterThanOrEqual(4.99, 5) // Returns false
 */
export const isGreaterThanOrEqual = (a: number, b: number): boolean => {
  return toDecimal(a).greaterThanOrEqualTo(toDecimal(b));
};

export const isLessThanOrEqual = (a: number, b: number): boolean => {
  return toDecimal(a).lessThanOrEqualTo(toDecimal(b));
};
export const isEqual = (a: number, b: number): boolean => {
  return toDecimal(a).equals(toDecimal(b));
};

/**
 * Formats a number into a shortened string representation with K (thousands) or M (millions)
 * Uses Decimal.js for precise calculations
 * @param value - Number to format
 * @returns Formatted string (e.g., "1.2K" or "3.4M")
 * @example
 * formatShortNumber(1234) // Returns "1.2K"
 * formatShortNumber(1234567) // Returns "1.2M"
 * formatShortNumber(999) // Returns "999"
 */
export const formatShortNumber = (value: number | null | undefined): string => {
  // Handle null/undefined
  if (value === null || value === undefined) return "0";

  const decimalValue = toDecimal(value);
  const absValue = decimalValue.abs();
  const sign = decimalValue.isNegative() ? "-" : "";

  const million = new Decimal(1000000);
  const thousand = new Decimal(1000);

  if (absValue.greaterThanOrEqualTo(million)) {
    return (
      sign +
      absValue
        .dividedBy(million)
        .toDecimalPlaces(1)
        .toString()
        .replace(/\.0$/, "") +
      "M"
    );
  }

  if (absValue.greaterThanOrEqualTo(thousand)) {
    return (
      sign +
      absValue
        .dividedBy(thousand)
        .toDecimalPlaces(1)
        .toString()
        .replace(/\.0$/, "") +
      "K"
    );
  }

  // For numbers less than 1000, return the whole number
  return sign + absValue.toDecimalPlaces(0).toString();
};

/**
 * Determines if a numerical value is effectively zero within a specified tolerance.
 * @param value - The numerical value to check.
 * @param epsilon - The tolerance range around zero. Defaults to 0.01 (one cent).
 * @returns True if the value is within [-epsilon, +epsilon], else false.
 */
export const nearZero = (value: number, epsilon: number = 0.01): boolean => {
  return new Decimal(value).abs().lessThanOrEqualTo(epsilon);
};

/**
 * Converts a numerical value to zero if it's effectively zero within a specified tolerance.
 * @param value - The numerical value to convert.
 * @param epsilon - The tolerance range around zero. Defaults to 0.01 (one cent).
 * @returns Zero if the value is within [-epsilon, +epsilon], else the original value.
 */
export const toNearZero = (value: number, epsilon: number = 0.01): number => {
  return nearZero(value, epsilon) ? 0 : value;
};

/**
 * Calculates the display subtotal by rounding individual line item amounts first.
 * This ensures that the displayed subtotal matches what a customer would calculate
 * by adding the displayed line item amounts themselves.
 *
 * For example:
 * Line items:
 * - Item 1: 100.777 -> 100.78
 * - Item 2: 200.224 -> 200.22
 * Display subtotal: 301.00 (matches sum of displayed amounts)
 *
 * Instead of:
 * Precise sum: 301.001 -> 301.00
 *
 * @param lineItems - Array of line items with prices and discounts
 * @param subtotalAfterDiscounts - The precise subtotal for validation
 * @param epsilon - The tolerance within which the precise values must match
 * @returns The display-formatted subtotal that matches customer calculations
 */

export const createDisplaySubTotal = (
  lineItems: JobLineItem[],
  withDiscounts = false,
  epsilon = 0.01
): number => {
  let preciseSubTotal = withDiscounts
    ? calculateJob.subtotalAfterDiscounts(lineItems).value
    : calculateJob.subtotal(lineItems).value;

  // Calculate display total by rounding each line item first
  const displayTotal = lineItems.reduce((sum, item) => {
    const itemPrice = withDiscounts
      ? calculateLineItem.finalPrice(getDiscountInputs(item)).value
      : toDecimal(item.price);
    // Round each line item to 2 decimal places before adding
    const roundedItemPrice = itemPrice.toDecimalPlaces(2);
    return sum.plus(roundedItemPrice);
  }, new Decimal(0));

  // Validate if precise values match within epsilon
  if (!displayTotal.minus(preciseSubTotal).abs().lessThanOrEqualTo(epsilon)) {
    // TODO log an error for debugging purposes
    // This means a more than unacceptable error in the calculation
    return preciseSubTotal.toDecimalPlaces(2).toNumber();
  }

  return displayTotal.toNumber();
};

/**
 * Splits the discounted display subtotal into mounts (specimen line items)
 * and services (everything else), rounding each line the same way
 * createDisplaySubTotal does so the two parts add up to Sub Total.
 */
export const createDisplayMountsServicesSubTotals = (
  lineItems: JobLineItem[]
): { mounts: number; services: number; hasBoth: boolean } => {
  const sumRounded = (items: JobLineItem[]) =>
    items
      .reduce(
        (sum, item) =>
          sum.plus(
            calculateLineItem
              .finalPrice(getDiscountInputs(item))
              .value.toDecimalPlaces(2)
          ),
        new Decimal(0)
      )
      .toNumber();

  const mountItems = lineItems.filter((item) => "specimen" in item);
  // Card processing fees are neither mounts nor services: they never count
  // towards the Services figure and never trigger the split on their own.
  const serviceItems = lineItems.filter(
    (item) => !("specimen" in item) && !isProcessingFeeLineItem(item)
  );

  return {
    mounts: sumRounded(mountItems),
    services: sumRounded(serviceItems),
    // Only worth splitting when the job mixes the two; a mounts-only or
    // services-only job just shows the total.
    hasBoth: mountItems.length > 0 && serviceItems.length > 0,
  };
};

/**
 * Calculates the display grand total by rounding the subtotal and tax individually.
 * This ensures that the displayed grand total matches what a customer would calculate
 * by adding the displayed subtotal and tax amounts themselves.
 *
 * For example:
 * - Precise subtotal: 980.7484125
 * - Precise tax: 78.375
 * - Precise total: 1059.1234125
 *
 * Display values:
 * - Rounded subtotal: 980.75
 * - Rounded tax: 78.38
 * - Display total: 1059.13 (matches what customer calculates)
 *
 * This prevents confusion where a customer might see:
 * Subtotal: 980.75
 * Tax: 78.38
 * Total: 1059.12 (precise but doesn't match visible components)
 *
 * Return the logical display grand total that matches customer calculations within the epsilon tolerance
 *
 * @param subtotalAfterDiscounts - The subtotal after any discounts are applied
 * @param totalTax - The total tax amount
 * @param grandTotal - The precise grand total for validation
 * @param epsilon - The tolerance within which the precise values must match
 * @returns The display-formatted grand total that matches customer calculations
 */

export const calculateDisplayGrandTotal = (
  lineItems: JobLineItem[],
  subtotalAfterDiscounts: number,
  totalTax: number,
  grandTotal: number,
  epsilon = 0.01
): number => {
  const preciseSubtotal = new Decimal(subtotalAfterDiscounts);
  const preciseTax = new Decimal(totalTax);
  const preciseGrandTotal = new Decimal(grandTotal);

  // Validate if precise values match within epsilon
  const preciseSum = preciseSubtotal.plus(preciseTax);
  if (!preciseSum.minus(preciseGrandTotal).abs().lessThanOrEqualTo(epsilon)) {
    // TODO log an error for debugging purposes
    // This means a more than unacceptable error in the calculation even with precision
    // it signifies a bug in the calculation
    return new Decimal(grandTotal).toDecimalPlaces(2).toNumber();
  }

  // Round subtotal and tax individually and return their sum
  const roundedSubtotal = toDecimal(createDisplaySubTotal(lineItems, true));
  const roundedTax = preciseTax.toDecimalPlaces(2);
  return roundedSubtotal.plus(roundedTax).toNumber();
};

/**
 * Calculates the display remaining balance by rounding the grand total and payments individually.
 * This ensures that the displayed remaining balance matches what a customer would calculate
 * by subtracting the displayed total payments from the displayed grand total.
 *
 * For example:
 * - Precise grand total: 1059.1234125
 * - Precise payments sum: 500.4851
 * - Precise remaining balance: 558.6383125
 *
 * Display values:
 * - Rounded grand total: 1059.12
 * - Rounded payments sum: 500.49
 * - Display remaining: 558.63 (matches what customer calculates)
 *
 * This prevents confusion where a customer might see:
 * Grand Total: 1059.12
 * Payments: 500.49
 * Remaining: 558.64 (precise but doesn't match visible components)
 *
 * Return the logical display remaining balance that matches customer calculations within the epsilon tolerance
 *
 * @param lineItems - Array of line items for calculating the grand total
 * @param subtotalAfterDiscounts - The subtotal after any discounts are applied
 * @param totalTax - The total tax amount
 * @param grandTotal - The precise grand total
 * @param payments - Array of payments made
 * @param remainingBalance - The precise remaining balance for validation
 * @param epsilon - The tolerance within which the precise values must match
 * @returns The display-formatted remaining balance that matches customer calculations
 */

export const calculateDisplayRemainingBalance = (
  lineItems: JobLineItem[],
  subtotalAfterDiscounts: number,
  totalTax: number,
  grandTotal: number,
  payments: JobPayment[],
  remainingBalance: number,
  epsilon = 0.01
): number => {
  const roundedPaymentsSum = payments.reduce((sum, p) => {
    // Skip deleted payments
    if (p.deleted) {
      return sum;
    }
    const paymentTotal = toDecimal(p.total || p.amount).toDecimalPlaces(2);
    // Subtract any refunds to get effective amount
    const refunded = toDecimal(p.totalRefunded || 0).toDecimalPlaces(2);
    return sum.plus(paymentTotal.minus(refunded));
  }, new Decimal(0));

  const roundedGrandTotal = toDecimal(
    calculateDisplayGrandTotal(
      lineItems,
      subtotalAfterDiscounts,
      totalTax,
      grandTotal
    )
  );

  const roundedRemainingBalance = new Decimal(remainingBalance).toDecimalPlaces(
    2
  );

  const intendedUserFacingRemainingBalance =
    roundedGrandTotal.minus(roundedPaymentsSum);

  const comparison = intendedUserFacingRemainingBalance.minus(
    roundedRemainingBalance
  );

  if (!comparison.abs().lessThanOrEqualTo(epsilon)) {
    // TODO log an error for debugging purposes
    // This means a more than unacceptable error in the calculation even with precision
    // it signifies a bug in the calculation
    return new Decimal(remainingBalance).toDecimalPlaces(2).toNumber();
  }

  return intendedUserFacingRemainingBalance.toNumber();
};

// WE DONT USE THIS RIGHT NOW, IF THERE IS A DIFFERENCE WE WILL EXPLAIN

/**
 * Calculates the display total for a payment by rounding individual components first.
 * This ensures that the displayed total matches what a customer would calculate
 * by adding the displayed amount, processing fee, and tax themselves.
 *
 * For example:
 * - Precise amount: 427.5
 * - Precise processing fee: 12.78225
 * - Precise tax: 1.054535625
 * - Precise total: 441.336785625
 *
 * Display values:
 * - Rounded amount: 427.50
 * - Rounded processing fee: 12.78
 * - Rounded tax: 1.05
 * - Display total: 441.33 (matches what customer calculates)
 *
 * This prevents confusion where a customer might see:
 * Amount: 427.50
 * Processing Fee: 12.78
 * Tax: 1.05
 * Total: 441.34 (precise but doesn't match visible components)
 *
 * @param payment - The payment object containing amount, processing fee, tax, and total
 * @param epsilon - The tolerance within which the precise values must match
 * @returns The display-formatted payment total that matches customer calculations
 */

export const calculatePaymentTotalAsDisplay = (
  payment: JobPayment,
  epsilon = 0.01
): number => {
  // Get precise total for validation

  // old payments don't have total
  const preciseTotal = toDecimal(payment.total || payment.amount);

  // Round each component individually to match display format
  const roundedAmount = toDecimal(payment.amount).toDecimalPlaces(2);
  const roundedProcessingFee = toDecimal(payment.processingFee).toDecimalPlaces(
    2
  );
  const roundedTax = toDecimal(payment.tax).toDecimalPlaces(2);

  // Sum the rounded components to match what a customer would calculate
  const displayTotal = roundedAmount
    .plus(roundedProcessingFee)
    .plus(roundedTax);

  // Validate if precise values match within epsilon
  if (!displayTotal.minus(preciseTotal).abs().lessThanOrEqualTo(epsilon)) {
    // TODO log an error for debugging purposes
    // This means a more than unacceptable error in the calculation

    return preciseTotal.toDecimalPlaces(2).toNumber();
  }

  return displayTotal.toNumber();
};

export const calculateTotalPaidAsDisplay = (payments: JobPayment[]): number => {
  return calculateJob
    .totalPayments(payments)
    .value.toDecimalPlaces(2)
    .toNumber();
};

export const sumClientAllRemainingBalances = (jobs: ClientJobV2[]): number => {
  const total = jobs.reduce((sum, job) => {
    return sum.plus(toDecimal(job.remainingBalance));
  }, new Decimal(0));

  return total.toDecimalPlaces(2).toNumber();
};

/**
 * Calculates payment amounts for a payment plan
 * Distributes the grand total across the specified number of payments
 * The last payment handles any rounding differences
 *
 * @param grandTotal - The total amount to be split across payments
 * @param numberOfPayments - How many payments to create
 * @param monthsBetweenPayments - Months between each payment (stored for contract reference)
 * @returns Array of payment amounts in order, with the last payment handling rounding
 *
 * @example
 * calculatePaymentPlan(1000, 3, 4) // Returns [333.33, 333.33, 333.34]
 * calculatePaymentPlan(999.99, 2, 6) // Returns [500.00, 499.99]
 * calculatePaymentPlan(100, 4, 1) // Returns [25.00, 25.00, 25.00, 25.00]
 */
export const calculatePaymentPlan = (
  grandTotal: number,
  numberOfPayments: number
): number[] => {
  // Input validation using existing utility functions
  if (isLessThan(numberOfPayments, 1)) {
    throw new Error("Number of payments must be greater than 0");
  }

  if (!isGreaterThan(grandTotal, 0)) {
    return Array(numberOfPayments).fill(0);
  }

  const totalDecimal = toDecimal(grandTotal);
  const paymentsCount = toDecimal(numberOfPayments);

  // Calculate the base payment amount
  const basePayment = totalDecimal.dividedBy(paymentsCount);
  const basePaymentRounded = basePayment.toDecimalPlaces(2);

  // Create array with base payment amounts
  const payments: number[] = Array(numberOfPayments).fill(
    basePaymentRounded.toNumber()
  );

  // Calculate total of rounded payments
  const totalRoundedPayments = basePaymentRounded.times(paymentsCount);

  // Calculate the difference due to rounding
  const difference = totalDecimal.minus(totalRoundedPayments);

  // Add the difference to the last payment to ensure total accuracy
  if (!isEqual(difference.toNumber(), 0)) {
    payments[numberOfPayments - 1] = calculateAddition(
      payments[numberOfPayments - 1],
      difference.toNumber()
    );
  }

  return payments;
};

/**
 * Adjusts a payment plan by setting a specific payment to a new amount
 * and redistributing the remaining balance across the other payments
 *
 * @param currentPayments - Current array of payment amounts
 * @param paymentIndex - Index of the payment to adjust (0-based)
 * @param newAmount - New amount for the specified payment
 * @param grandTotal - Original grand total to maintain accuracy
 * @returns New array of payment amounts with the adjustment
 *
 * @example
 * // Original: [333.33, 333.33, 333.34] for $1000 total
 * adjustPaymentPlan([333.33, 333.33, 333.34], 0, 500, 1000)
 * // Returns: [500.00, 250.00, 250.00] - first payment set to $500, remaining $500 split between others
 */
export const adjustPaymentPlan = (
  currentPayments: number[],
  paymentIndex: number,
  newAmount: number,
  grandTotal: number
): number[] => {
  // Input validation
  if (paymentIndex < 0 || paymentIndex >= currentPayments.length) {
    throw new Error("Payment index out of range");
  }

  if (isLessThan(newAmount, 0)) {
    throw new Error("Payment amount cannot be negative");
  }

  if (!isGreaterThan(grandTotal, 0)) {
    return [...currentPayments];
  }

  const totalDecimal = toDecimal(grandTotal);
  const adjustedAmountDecimal = toDecimal(newAmount);

  // Check if the new amount exceeds the grand total
  if (isGreaterThan(newAmount, grandTotal)) {
    throw new Error("Payment amount cannot exceed the grand total");
  }

  // Calculate remaining amount to distribute
  const remainingAmount = totalDecimal.minus(adjustedAmountDecimal);
  const otherPaymentsCount = currentPayments.length - 1;

  // If there's only one payment, just return the grand total
  if (otherPaymentsCount === 0) {
    return [grandTotal];
  }

  // Calculate new base amount for other payments
  const baseAmountForOthers = remainingAmount.dividedBy(otherPaymentsCount);
  const baseAmountRounded = baseAmountForOthers.toDecimalPlaces(2);

  // Create new payments array
  const newPayments: number[] = [];

  // Fill with base amounts for other payments
  for (let i = 0; i < currentPayments.length; i++) {
    if (i === paymentIndex) {
      newPayments[i] = adjustedAmountDecimal.toNumber();
    } else {
      newPayments[i] = baseAmountRounded.toNumber();
    }
  }

  // Calculate total of rounded other payments
  const totalOtherPayments = baseAmountRounded.times(otherPaymentsCount);

  // Calculate difference due to rounding
  const difference = remainingAmount.minus(totalOtherPayments);

  // Add the difference to the last payment that's not the adjusted one
  if (!isEqual(difference.toNumber(), 0)) {
    // Find the last payment that's not the adjusted payment
    const lastOtherIndex =
      paymentIndex === currentPayments.length - 1
        ? currentPayments.length - 2
        : currentPayments.length - 1;

    newPayments[lastOtherIndex] = calculateAddition(
      newPayments[lastOtherIndex],
      difference.toNumber()
    );
  }

  return newPayments;
};

/**
 * Redistributes remaining balance across other payments when one payment is fixed
 * This function keeps the specified payment fixed and redistributes the remaining balance evenly
 *
 * @param currentAmounts - Current array of payment amounts
 * @param fixedIndex - Index of the payment to keep fixed (0-based)
 * @param fixedAmount - Amount for the fixed payment
 * @param grandTotal - Original grand total to maintain accuracy
 * @returns New array of payment amounts with redistribution
 *
 * @example
 * // Grand total: $1200, fix first payment at $500
 * redistributeRemainingBalance([600, 600], 0, 500, 1200)
 * // Returns: [500, 700] - first payment fixed at $500, remaining $700 goes to second payment
 *
 * // Grand total: $1200, fix first payment at $300, 3 payments total
 * redistributeRemainingBalance([400, 400, 400], 0, 300, 1200)
 * // Returns: [300, 450, 450] - first payment fixed at $300, remaining $900 split between others
 */
export const redistributeRemainingBalance = (
  currentAmounts: number[],
  fixedIndex: number,
  fixedAmount: number,
  grandTotal: number
): number[] => {
  const newAmounts = [...currentAmounts];
  newAmounts[fixedIndex] = fixedAmount;

  const remainingBalance = grandTotal - fixedAmount;
  const otherPaymentCount = currentAmounts.length - 1;

  if (otherPaymentCount <= 0) {
    return newAmounts;
  }

  // Distribute remaining balance evenly across other payments
  const baseAmount =
    Math.floor((remainingBalance * 100) / otherPaymentCount) / 100;
  const remainder = remainingBalance - baseAmount * otherPaymentCount;

  let remainderDistributed = 0;
  for (let i = 0; i < currentAmounts.length; i++) {
    if (i !== fixedIndex) {
      newAmounts[i] = baseAmount;
      // Add remainder cents to first few payments
      if (remainderDistributed < Math.round(remainder * 100)) {
        newAmounts[i] += 0.01;
        remainderDistributed += 1;
      }
    }
  }

  return newAmounts;
};

/**
 * Distributes the grand total across each payment schedule item evenly or as evenly as possible by the num of total payments
 * Guarantees that the sum of all payments equals the grand total exactly (no rounding errors)
 *
 * @param numOfPayments - Number of payments to distribute across
 * @param grandTotal - Total amount to distribute
 * @returns Array of payment amounts that sum to grandTotal exactly
 */
export const distributeBalanceEvenly = (
  numOfPayments: number,
  grandTotal: number
): number[] => {
  // Input validation
  if (numOfPayments <= 0) {
    return [];
  }

  if (!isGreaterThan(grandTotal, 0)) {
    return Array(numOfPayments).fill(0);
  }

  const totalDecimal = toDecimal(grandTotal);
  const paymentsCount = toDecimal(numOfPayments);

  // Calculate base amount using Decimal.js (floor to cents)
  const totalCents = totalDecimal.times(100);
  const baseAmountCents = totalCents.dividedBy(paymentsCount).floor();
  const baseAmount = baseAmountCents.dividedBy(100);

  // Create array with base amounts
  const payments: number[] = Array(numOfPayments).fill(baseAmount.toNumber());

  // Calculate how much we're short due to flooring
  const totalBaseCents = baseAmountCents.times(paymentsCount);
  const remainderCents = totalCents.minus(totalBaseCents).toNumber();

  // Distribute the remainder cents across the first few payments
  for (let i = 0; i < remainderCents && i < numOfPayments; i++) {
    payments[i] = toDecimal(payments[i]).plus(0.01).toNumber();
  }

  return payments;
};

/**
 * Calculate total expenses from materials assigned to line items
 * Sums costPrice * quantity for all materials across all line items
 */
export const calculateTotalExpenses = (lineItems: JobLineItem[]): number => {
  const total = lineItems.reduce((sum, item) => {
    const materials = item.materials ?? [];
    const materialTotal = materials.reduce((mSum, material) => {
      return mSum.plus(
        toDecimal(material.costPrice).times(toDecimal(material.quantity))
      );
    }, new Decimal(0));
    return sum.plus(materialTotal);
  }, new Decimal(0));

  return createDecimalValue(total).raw();
};

/**
 * Calculate the labor cost of a time entry: (minutes / 60) * hourly shop rate.
 * Returns null when the team member has no shop rate, in which case the entry
 * logs time but carries no cost.
 */
export const calculateTimeEntryCost = (
  durationMinutes: number,
  shopRate: number | null | undefined
): number | null => {
  if (shopRate === null || shopRate === undefined) return null;
  const total = toDecimal(durationMinutes)
    .dividedBy(60)
    .times(toDecimal(shopRate))
    .toDecimalPlaces(2);
  return createDecimalValue(total).raw();
};
